import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { automaticChargeApi } from 'services/apiDigitalTicket/automaticCharge';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';

import * as AutomaticChargeActions from './actions';
import { AutomaticChargeTypes } from './types';

/**
 * Shows the API error in a snackbar: the mapped message when the internal code is known,
 * otherwise the message the backend sent, before falling back to the generic one.
 * @param {any} error the caught request error
 * @return {Generator} the snackbar effect
 */
function* showError(error: any): any {
  const data = error && error.response && error.response.data;
  const mapped = errorMessagersList(data && data.internalCode);
  const isGeneric = mapped === errorMessagersList('error.generic');
  const message = isGeneric && data && data.message ? data.message : mapped;

  yield put(SnackbarsActions.setOpen({ open: true, type: 'error', message }));
}

function* getStatus(): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(automaticChargeApi.getStatus, {
      params: { transportOperatorId: transportOperator.id },
    });

    if (response.status === 200) {
      yield put(
        AutomaticChargeActions.getAutomaticChargeStatusSuccess({
          statusData: response.data,
        }),
      );
    }
    yield put(DialogsActions.setClose());
  } catch (error) {
    console.error('automaticCharge - getStatus - error', error);
    yield* showError(error);
    yield put(AutomaticChargeActions.getAutomaticChargeStatusFailure());
    yield put(DialogsActions.setClose());
  }
}

function* acceptContract({
  payload,
  meta,
}: ReturnType<
  typeof AutomaticChargeActions.acceptAutomaticChargeContractRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Registrando aceite do contrato',
      }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(automaticChargeApi.acceptContract, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(AutomaticChargeActions.acceptAutomaticChargeContractSuccess());
      meta.promise.resolve(response);
    }
    yield put(DialogsActions.setClose());
  } catch (error) {
    console.error('automaticCharge - acceptContract - error', error);
    yield* showError(error);
    yield put(AutomaticChargeActions.acceptAutomaticChargeContractFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* enable({
  payload,
  meta,
}: ReturnType<
  typeof AutomaticChargeActions.enableAutomaticChargeRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Habilitando a recarga automática',
      }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(automaticChargeApi.enable, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(AutomaticChargeActions.enableAutomaticChargeSuccess());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Recarga automática habilitada',
        }),
      );
      meta.promise.resolve(response);
    }
    yield put(DialogsActions.setClose());
  } catch (error) {
    console.error('automaticCharge - enable - error', error);
    yield* showError(error);
    yield put(AutomaticChargeActions.enableAutomaticChargeFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* updatePayoutAccount({
  payload,
  meta,
}: ReturnType<
  typeof AutomaticChargeActions.updateAutomaticChargePayoutAccountRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Salvando conta de repasse' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(automaticChargeApi.updatePayoutAccount, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        AutomaticChargeActions.updateAutomaticChargePayoutAccountSuccess(),
      );
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Conta de repasse atualizada',
        }),
      );
      meta.promise.resolve(response);
    }

    yield put(DialogsActions.setClose());
  } catch (error) {
    console.error('automaticCharge - updatePayoutAccount - error', error);
    yield* showError(error);
    yield put(
      AutomaticChargeActions.updateAutomaticChargePayoutAccountFailure(),
    );
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* dailyReport({
  payload,
}: ReturnType<
  typeof AutomaticChargeActions.getAutomaticChargeDailyReportRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(automaticChargeApi.dailyReport, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        AutomaticChargeActions.getAutomaticChargeDailyReportSuccess({
          dailyReports: Array.isArray(response.data) ? response.data : [],
        }),
      );
    }
    yield put(DialogsActions.setClose());
  } catch (error) {
    console.error('automaticCharge - dailyReport - error', error);
    yield* showError(error);
    yield put(AutomaticChargeActions.getAutomaticChargeDailyReportFailure());
    yield put(DialogsActions.setClose());
  }
}

function* historyDetail({
  payload,
}: ReturnType<
  typeof AutomaticChargeActions.getAutomaticChargeHistoryDetailRequest
>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(automaticChargeApi.historyDetail, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        AutomaticChargeActions.getAutomaticChargeHistoryDetailSuccess({
          historyDetail: Array.isArray(response.data) ? response.data : [],
          historyDetailTotalCount:
            parseInt(response.headers.totalcount, 10) || 0,
        }),
      );
    }
  } catch (error) {
    console.error('automaticCharge - historyDetail - error', error);
    yield* showError(error);
    yield put(AutomaticChargeActions.getAutomaticChargeHistoryDetailFailure());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const automaticChargeSagas = [
  takeLatest(
    AutomaticChargeTypes.GET_AUTOMATIC_CHARGE_STATUS_REQUEST,
    getStatus,
  ),
  takeLatest(
    AutomaticChargeTypes.ACCEPT_AUTOMATIC_CHARGE_CONTRACT_REQUEST,
    acceptContract,
  ),
  takeLatest(AutomaticChargeTypes.ENABLE_AUTOMATIC_CHARGE_REQUEST, enable),
  takeLatest(
    AutomaticChargeTypes.UPDATE_AUTOMATIC_CHARGE_PAYOUT_ACCOUNT_REQUEST,
    updatePayoutAccount,
  ),
  takeLatest(
    AutomaticChargeTypes.GET_AUTOMATIC_CHARGE_DAILY_REPORT_REQUEST,
    dailyReport,
  ),
  takeLatest(
    AutomaticChargeTypes.GET_AUTOMATIC_CHARGE_HISTORY_DETAIL_REQUEST,
    historyDetail,
  ),
];
